import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { userApi } from '../api/userApi'
import { toast } from '@/hooks/use-toast'
import type { UserRole } from '@/types/entities'

export interface UserAdminParams {
  search?: string
  role?: UserRole
  limit?: number
  offset?: number
}

export const useUserAdmin = (params: UserAdminParams = {}) => {
  const queryClient = useQueryClient()
  const { search = '', role, limit = 25, offset = 0 } = params

  const usersQuery = useQuery({
    queryKey: ['users', { search, role, limit, offset }],
    queryFn: () => userApi.listUsers({ search, role, limit, offset }),
    // Paging and typing both change the key. Without this the table empties to a spinner between
    // every page and every keystroke, which reads as the data disappearing.
    placeholderData: keepPreviousData,
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword: string }) =>
      userApi.adminResetPassword(userId, newPassword),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', updatedUser.id] })
      toast({
        title: 'Password reset',
        description: `Password for ${updatedUser.email} has been reset successfully.`,
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error resetting password',
        description: error.response?.data?.message || 'Failed to reset password.',
        variant: 'destructive',
      })
    },
  })

  return {
    users: usersQuery.data?.data ?? [],
    total: usersQuery.data?.total ?? 0,
    isLoading: usersQuery.isLoading,
    isFetching: usersQuery.isFetching,
    isError: usersQuery.isError,
    error: usersQuery.error,
    refetch: usersQuery.refetch,
    resetPassword: resetPasswordMutation.mutate,
    isResettingPassword: resetPasswordMutation.isPending,
  }
}
